import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { ValueOf } from '@/helpers/types';
import type { ComputedRef, Ref } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { toValue } from 'vue';
import { computed, onMounted, onServerPrefetch } from 'vue';

import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { storeToRefs } from 'pinia';
import { useExperiment } from '@/libraries/experiment/composables/useExperiment';

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFreeOrAds = [MonetizationType.Flatrate, MonetizationType.Free, MonetizationType.Ads];

interface PromoText {
	/**
	 * Angled ribbon over square provider icons or
	 * drawer below wide provider icons.
	 */
	ribbon?: string;

	/**
	 * Ribbon text exclusively for wide provider icons
	 * which have room for a bit more text.
	 */
	wideRibbon?: string;

	/**
	 * Text below the square provider in GridOffer (next to 4k/quality).
	 * Text above the price in ListOffer.
	 */
	offerText?: string;

	/** Text that appears on the backdrop play button. */
	longText?: string;

	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;

	/** `YYYY-MM-DD` format. */
	endDate: ISO8601;

	/** 3 character shortname of the promoted provider. */
	provider: string;

	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;

	/** Disclaimer text - for additional Terms and Conditions, passed into the DisclaimerButton */
	disclaimerText?: string;

	/** Secondary disclaimer text for BuyBoxOfferNew - for additional Terms and Conditions, passed into the DisclaimerButton */
	offerDisclaimerText?: string;

	/** Suppress the provider promotion on the backdrop play button --> was a special requirement from HBO Max GB promotion to only have the buybox offer promotions, but assumption is that this could be more common */
	hideBackdropPromo?: boolean;

	experimentName?: string; // for testing purposes to link the promo to a specific experiment variant
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

// Change uk to gb in the WebLocale list and return it upper case to match store country
type PromotionLocale = Uppercase<Exclude<keyof typeof WebLocale, 'uk'> | 'gb'>;

const promotions = {
	AT: [
		{
			ribbon: 'Angebot',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Ab 4,99 €/M für 3 Monate*',
			disclaimerText:
				'*Aktion endet am 28.07.26. Gilt nur für neue teilnahmeberechtigte Abonnenten. Ab 18 Jahren. Nach 3 Monaten wird Disney+ zum Standard mit Werbung Preis von 6,99 €/Monat verlängert, bis du kündigst.',
			offerDisclaimerText:
				'*Aktion endet am 28.07.26. Gilt nur für neue teilnahmeberechtigte Abonnenten. Ab 18 Jahren. Nach 3 Monaten wird Disney+ zum Standard mit Werbung Preis von 6,99 €/Monat verlängert, bis du kündigst.',
		},
	],
	BE: [
		{
			ribbon: 'Actie',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Vanaf € 4,99 per maand voor 3 maanden',
			disclaimerText:
				'Aanbieding eindigt op 28/07/26. Alleen voor nieuwe en daarvoor in aanmerking komende terugkerende abonnees. 18+. Na 3 maanden wordt het abonnement automatisch verlengd tegen de dan geldende prijs van het gekozen abonnement, tenzij je vóór het einde van de actieperiode opzegt.',
			offerDisclaimerText:
				'Aanbieding eindigt op 28/07/26. Alleen voor nieuwe en daarvoor in aanmerking komende terugkerende abonnees. 18+. Na 3 maanden wordt het abonnement automatisch verlengd tegen de dan geldende prijs van het gekozen abonnement, tenzij je vóór het einde van de actieperiode opzegt.',
		},
	],
	CH: [
		{
			ribbon: 'Angebot',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Ab CHF 6.90 pro Monat',
			disclaimerText:
				'Angebot endet am 28.07.26. Nur für neue und berechtigte zurückkehrende Abonnenten. Mindestalter 18 Jahre. Nach 3 Monaten verlängert sich das Abonnement automatisch zum dann geltenden Preis des gewählten Abos, sofern es nicht vorher gekündigt wird.',
			offerDisclaimerText:
				'Angebot endet am 28.07.26. Nur für neue und berechtigte zurückkehrende Abonnenten. Mindestalter 18 Jahre. Nach 3 Monaten verlängert sich das Abonnement automatisch zum dann geltenden Preis des gewählten Abos, sofern es nicht vorher gekündigt wird.',
		},
	],
	CZ: [
		{
			ribbon: 'Oferta',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Od 149 Kč měsíčně na 3 měsíce',
			disclaimerText:
				'18+ bez aktivního předplatného Disney+. Po 3 měs. se obnoví dle vašeho tarifu (Standard 219 CZK; Premium 359 CZ), pokud nezrušíte.',
			offerDisclaimerText:
				'18+ bez aktivního předplatného Disney+. Po 3 měs. se obnoví dle vašeho tarifu (Standard 219 CZK; Premium 359 CZ), pokud nezrušíte.',
		},
	],
	DE: [
		{
			ribbon: 'Angebot',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Ab 4,99 €/M für 3 Monate*',
			disclaimerText:
				'*Aktion endet am 28.07.26. Gilt nur für neue teilnahmeberechtigte Abonnenten. Ab 18 Jahren. Nach 3 Monaten wird Disney+ zum Standard mit Werbung Preis von 6,99 €/Monat verlängert, bis du kündigst.',
			offerDisclaimerText:
				'*Aktion endet am 28.07.26. Gilt nur für neue teilnahmeberechtigte Abonnenten. Ab 18 Jahren. Nach 3 Monaten wird Disney+ zum Standard mit Werbung Preis von 6,99 €/Monat verlängert, bis du kündigst.',
		},
	],
	ES: [
		{
			ribbon: 'Ver oferta',
			startDate: '2026-09-10',
			endDate: '2026-09-30',
			provider: 'sst',
			longText: 'desde 4,99 € al mes durante 6 meses',
			disclaimerText:
				'La oferta finaliza el 12/10/2026. Plan Estándar por 4,99 € al mes durante 6 meses. Después, la suscripción se renovará automáticamente al precio mensual completo (actualmente 8,99 €), salvo cancelación. Cancela cuando quieras; la cancelación será efectiva al final del periodo de facturación. Solo para clientes nuevos y antiguos clientes que cumplan los requisitos. Se aplican términos y condiciones.',
			offerDisclaimerText:
				'La oferta finaliza el 12/10/2026. Plan Estándar por 4,99 € al mes durante 6 meses. Después, la suscripción se renovará automáticamente al precio mensual completo (actualmente 8,99 €), salvo cancelación. Cancela cuando quieras; la cancelación será efectiva al final del periodo de facturación. Solo para clientes nuevos y antiguos clientes que cumplan los requisitos. Se aplican términos y condiciones.',
		},
		{
			ribbon: 'Oferta',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'desde 4,99 € al mes durante 3 meses',
			disclaimerText:
				'Solo para mayores de 18 años sin suscripción activa. Tras 3 meses, se renueva al precio actual del plan elegido (desde 6,99€ Estándar con anuncios) a menos que la canceles antes.',
			offerDisclaimerText:
				'Solo para mayores de 18 años sin suscripción activa. Tras 3 meses, se renueva al precio actual del plan elegido (desde 6,99€ Estándar con anuncios) a menos que la canceles antes.',
		},
	],
	FR: [
		{
			ribbon: 'Offre',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'À partir de 4,99 €/mois pendant 3 mois',
			disclaimerText:
				'Offre pour les nouveaux abonnés majeurs. Après 3 mois, reconduction au tarif plein sauf si résilié avant',
			offerDisclaimerText:
				'Offre pour les nouveaux abonnés majeurs. Après 3 mois, reconduction au tarif plein sauf si résilié avant',
		},
	],
	GR: [
		{
			ribbon: 'Προσφορά',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Από μόλις 7,99 € τον μήνα',
			disclaimerText:
				'Η προσφορά λήγει στις 28/07/26. Ισχύει μόνο για νέους συνδρομητές και για επιλέξιμους συνδρομητές που επιστρέφουν. Απαραίτητη η ηλικία των 18 ετών και άνω. Μετά από 3 μήνες, η συνδρομή ανανεώνεται αυτόματα στην τότε ισχύουσα τιμή του επιλεγμένου προγράμματος, εκτός εάν την ακυρώσετε πριν από τότε.',
			offerDisclaimerText:
				'Η προσφορά λήγει στις 28/07/26. Ισχύει μόνο για νέους συνδρομητές και για επιλέξιμους συνδρομητές που επιστρέφουν. Απαραίτητη η ηλικία των 18 ετών και άνω. Μετά από 3 μήνες, η συνδρομή ανανεώνεται αυτόματα στην τότε ισχύουσα τιμή του επιλεγμένου προγράμματος, εκτός εάν την ακυρώσετε πριν από τότε.',
		},
	],
	HR: [
		{
			ribbon: 'Ponuda',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Od 6,99 € mjesečno tijekom prva 3 mjeseca',
			disclaimerText:
				'Ponuda vrijedi do 28. 7. 2026. Samo za nove pretplatnike i prethodne pretplatnike koji ispunjavaju uvjete. Potrebno je imati najmanje 18 godina. Nakon 3 mjeseca pretplata se automatski obnavlja po tada važećoj cijeni odabranog paketa, osim ako je ne otkažete prije isteka tog razdoblja.',
			offerDisclaimerText:
				'Ponuda vrijedi do 28. 7. 2026. Samo za nove pretplatnike i prethodne pretplatnike koji ispunjavaju uvjete. Potrebno je imati najmanje 18 godina. Nakon 3 mjeseca pretplata se automatski obnavlja po tada važećoj cijeni odabranog paketa, osim ako je ne otkažete prije isteka tog razdoblja.',
		},
	],
	HU: [
		{
			ribbon: 'Ajánlat',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Havi 1990 Ft-tól 3 hónapig',
			disclaimerText:
				'18 éven felülieknek aktív Disney+-előfizetés nélkül. 3 hónap után automatikusan megújul: Standard 2990Ft; Prémium 4890Ft áron, ha le nem mondod',
			offerDisclaimerText:
				'18 éven felülieknek aktív Disney+-előfizetés nélkül. 3 hónap után automatikusan megújul: Standard 2990Ft; Prémium 4890Ft áron, ha le nem mondod',
		},
	],
	IE: [
		{
			ribbon: 'Offer',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'From £3.99/mo for 3 months',
			disclaimerText:
				'Offer ends 28/07/26. New and eligible returning subscribers only. Must be 18+. After 3 months auto-renews at the then-current monthly price of the chosen plan (currently: Standard With Ads €8.99, Standard €11.99, Premium €15.99) unless you cancel before then',
			offerDisclaimerText:
				'Offer ends 28/07/26. New and eligible returning subscribers only. Must be 18+. After 3 months auto-renews at the then-current monthly price of the chosen plan (currently: Standard With Ads €8.99, Standard €11.99, Premium €15.99) unless you cancel before then',
		},
	],
	IT: [
		{
			ribbon: 'Offerta',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Da 4,99 € al mese per 3 mesi',
			disclaimerText:
				"Solo per i nuovi abbonati Disney+ e per precedenti abbonamenti se riattivati. Per abbonarti devi avere almeno 18 anni. Dopo 3 mesi l'abbonamento si rinnova automaticamente al prezzo mensile del piano scelto in vigore in quel momento (a partire da 6,99 €), a meno che tu non disdica prima.",
			offerDisclaimerText:
				"Solo per i nuovi abbonati Disney+ e per precedenti abbonamenti se riattivati. Per abbonarti devi avere almeno 18 anni. Dopo 3 mesi l'abbonamento si rinnova automaticamente al prezzo mensile del piano scelto in vigore in quel momento (a partire da 6,99 €), a meno che tu non disdica prima.",
		},
	],
	NL: [
		{
			ribbon: 'Actie',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Vanaf € 4,99 per maand voor 3 maanden',
			disclaimerText:
				'Aanbieding eindigt op 28/07/26. Alleen voor nieuwe en daarvoor in aanmerking komende terugkerende abonnees. 18+. Na 3 maanden wordt het abonnement automatisch verlengd tegen de dan geldende prijs van het gekozen abonnement, tenzij je vóór het einde van de actieperiode opzegt.',
			offerDisclaimerText:
				'Aanbieding eindigt op 28/07/26. Alleen voor nieuwe en daarvoor in aanmerking komende terugkerende abonnees. 18+. Na 3 maanden wordt het abonnement automatisch verlengd tegen de dan geldende prijs van het gekozen abonnement, tenzij je vóór het einde van de actieperiode opzegt.',
		},
	],
	PL: [
		{
			ribbon: 'Oferta',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Od 23,99zł miesięcznie przez 3 miesiące',
			disclaimerText:
				'Wymagane 18 lat i brak aktywnej subskrypcji. Wcześniej: Standard 34,99zł; Premium 34,99 zł Po 3 mc odn. w cenie wybr. planu (Standard 34,99 zł; Premium 59,99 zł) o ile nie anulujesz',
			offerDisclaimerText:
				'Wymagane 18 lat i brak aktywnej subskrypcji. Wcześniej: Standard 34,99zł; Premium 34,99 zł Po 3 mc odn. w cenie wybr. planu (Standard 34,99 zł; Premium 59,99 zł) o ile nie anulujesz',
		},
	],
	PT: [
		{
			ribbon: 'Oferta',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'A partir de 4,99 € por 3 meses',
			disclaimerText:
				'A oferta termina em 28/07/26. Apenas para novos subscritores e antigos subscritores elegíveis. É necessário ter 18 anos ou mais. Após 3 meses, a subscrição renova-se automaticamente pelo preço em vigor nessa altura para o plano escolhido, salvo cancelamento antes dessa data.',
			offerDisclaimerText:
				'A oferta termina em 28/07/26. Apenas para novos subscritores e antigos subscritores elegíveis. É necessário ter 18 anos ou mais. Após 3 meses, a subscrição renova-se automaticamente pelo preço em vigor nessa altura para o plano escolhido, salvo cancelamento antes dessa data.',
		},
	],
	RO: [
		{
			ribbon: 'Oferta',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'De la 24,99 lei pe lună primele 3 luni',
			disclaimerText:
				'18+ fără abonament Disney+ activ. Anterior: Standard 34,99 lei; Premium 34,99 lei. După 3 luni,se reînnoiește la 34,99 lei (Standard)/ 59,99 lei (Premium), dacă nu anulezi.',
			offerDisclaimerText:
				'18+ fără abonament Disney+ activ. Anterior: Standard 34,99 lei; Premium 34,99 lei. După 3 luni,se reînnoiește la 34,99 lei (Standard)/ 59,99 lei (Premium), dacă nu anulezi.',
		},
	],
	SK: [
		{
			ribbon: 'Ponuka',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: 'Od 6,99 € mesačne na 3 mesiace',
			disclaimerText:
				'Platí pre používateľov od 18 rokov bez predplatného Disney+. Po 3 mesiacoch sa predplatné obnoví za 9,99 € Standard / 13,99 € Premium, ak ho nezrušíte.',
			offerDisclaimerText:
				'Platí pre používateľov od 18 rokov bez predplatného Disney+. Po 3 mesiacoch sa predplatné obnoví za 9,99 € Standard / 13,99 € Premium, ak ho nezrušíte.',
		},
	],
	TR: [
		{
			ribbon: 'Teklif',
			startDate: '2026-07-15',
			endDate: '2026-07-28',
			provider: 'dnp',
			longText: "3 Ay Boyunca Aylık 179,9 TL'den",
			disclaimerText:
				'Aktif Disney+ üyeliği olmayan, 18+ yaşındaki kullanıcılar için geçerlidir. İptal edilmezse 3 aydan sonra seçilen plana bağlı olarak aylık 249,90 TL ya da 449,90 TL',
			offerDisclaimerText:
				'Aktif Disney+ üyeliği olmayan, 18+ yaşındaki kullanıcılar için geçerlidir. İptal edilmezse 3 aydan sonra seçilen plana bağlı olarak aylık 249,90 TL ya da 449,90 TL',
		},
	],
} satisfies Partial<Record<PromotionLocale, PromoText[]>>;

type PromotionCountries = keyof typeof promotions;
type PromotionProviders = ValueOf<typeof promotions>[number]['provider'];

const promotionProviderIcons: Record<PromotionProviders, string> = {
	amp: `${JW_CONFIG.IMAGESCALER_URL}/icon/322992749/s100/amazonprime.webp`,
	prv: `${JW_CONFIG.IMAGESCALER_URL}/icon/322992749/s100/amazonprime.webp`,
	hst: `${JW_CONFIG.IMAGESCALER_URL}/icon/324950024/s100/hotstar.webp`,
	dnp: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	atp: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
	bng: `${JW_CONFIG.IMAGESCALER_URL}/icon/183470172/s100/binge.webp`,
	pmp: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	sst: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	mxx: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
	tbv: `${JW_CONFIG.IMAGESCALER_URL}/icon/313528601/s100/tubitv.webp`,
	cts: `${JW_CONFIG.IMAGESCALER_URL}/icon/48600036/s100/curiositystream.webp`,
	aho: `${JW_CONFIG.IMAGESCALER_URL}/icon/343788557/s100/amazonhbomax.webp`,
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>): {
	titleHasProviderPromotion: ComputedRef<boolean>;
	titleHasBackdropPromo: ComputedRef<boolean>;
	isPromotionOffer: (offer: TitleOfferFragment) => boolean;
	providerPromotion: Ref<PromoTextWithIcon | null>;
} {
	const titleDetailsStore = useTitleDetailsStore();
	const { setProviderPromotion } = titleDetailsStore;
	const { providerPromotion } = storeToRefs(titleDetailsStore);

	const { activeVariantWithControlGroup: primePlayPromoVariant } = useExperiment('EXP_PRIME_PLAY_PROMO');

	onServerPrefetch(() => setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(() => setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);
	const titleHasBackdropPromo = computed(() => {
		if (!titleHasProviderPromotion.value || providerPromotion.value?.hideBackdropPromo) return false;
		if (providerPromotion.value?.experimentName === 'EXP_PRIME_PLAY_PROMO')
			return primePlayPromoVariant.value === 'variant_1';
		return true;
	});

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFreeOrAds.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.shortName
		);
	}

	return {
		/** Is a provider promotion applicable to the current title - typically be play button (backdrop) promotion, or updating offer texts and ribbons. */
		titleHasProviderPromotion,

		/** Is a provider promotion applicable to the backdrop play button. */
		titleHasBackdropPromo,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const promos = promotions[country as PromotionCountries];

	if (!promos || promos.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = promos.find(promo =>
		title.offers.some(
			offer => flatrateOrFreeOrAds.includes(offer.monetizationType) && offer.package.shortName === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}
